import { useState } from 'react';
import { Box, IconButton, Popover, Tooltip, Typography } from '@mui/material';
import { DateRangeIcon } from 'components/CommonIcons/Icons';
import DateTimeRangePicker, { DEFAULT_FIXED_OPTIONS } from './DateTimeRangePicker';
import { formatDateRangeLabel, STANDARD_LABELS, TIMEZONE_MAP } from 'utils/common-util';
import moment from 'moment';

interface DateTimeRangeFilterProps {
    dateRangeType: string;
    customStartDate: Date | null;
    customEndDate: Date | null;
    onChange: (type: string, start?: Date, end?: Date) => void;
    timezone: string;
    fixedOptions?: { val: string; label: string }[];
    olderThan90DaysMode?: boolean;
    showCustomRange?: boolean;
}

const DateTimeRangeFilter = ({ dateRangeType, customStartDate, customEndDate, onChange, timezone = 'CST', fixedOptions, olderThan90DaysMode = false, showCustomRange = true }: DateTimeRangeFilterProps) => {
    const ianaTimezone = TIMEZONE_MAP[timezone];
    const [customRangeAnchorEl, setCustomRangeAnchorEl] = useState<null | HTMLElement>(null);
    const customRangeOpen = Boolean(customRangeAnchorEl);

    const openCustomRange = (e: React.MouseEvent<HTMLElement>) => setCustomRangeAnchorEl(e.currentTarget);
    const closeCustomRange = () => setCustomRangeAnchorEl(null);
    
    const options = fixedOptions ?? (DEFAULT_FIXED_OPTIONS as unknown as { val: string; label: string }[]);
    const selectedLabel = dateRangeType === 'custom' && customStartDate && customEndDate
        ? `${moment(customStartDate).tz(ianaTimezone).format("MM-DD-YYYY HH:mm:ss")} to ${moment(customEndDate).tz(ianaTimezone).format("MM-DD-YYYY HH:mm:ss")}`
        : (options.find(o => o.val === dateRangeType)?.label ?? formatDateRangeLabel(dateRangeType));

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={"Select Date Range"} placement="bottom">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }} onClick={openCustomRange}>
                    <Typography
                        sx={{
                            color: "#48B5ED",
                            fontSize: "14px",
                            fontWeight: 500,
                            fontFamily: "Montserrat",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {(() => {
                            if (dateRangeType === 'custom' && customStartDate && customEndDate) {
                                return `${moment(customStartDate).tz(ianaTimezone).format("MM-DD-YYYY HH:mm:ss")} to ${moment(customEndDate).tz(ianaTimezone).format("MM-DD-YYYY HH:mm:ss")}`;
                            }
                           if (STANDARD_LABELS[dateRangeType]) return STANDARD_LABELS[dateRangeType];

                            // For dynamic ranges
                            let clean = String(dateRangeType).replace(/_/g, ' ').replace(/^last\s+/i, '');
                            return `Last ${clean.charAt(0).toUpperCase() + clean.slice(1)}`;
                        })()}


                    </Typography>
                    <IconButton size="small" aria-label="Open Calendar" sx={{ p: 0 }}>
                        <DateRangeIcon />
                    </IconButton>
                </Box>
            </Tooltip>
            <Popover
                open={customRangeOpen}
                anchorEl={customRangeAnchorEl}
                onClose={closeCustomRange}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 1,
                            borderRadius: '8px',
                            boxShadow: '0px 4px 20px rgba(0,0,0,0.15)',
                            border: '1px solid #E0E0E0'
                        },
                    },
                }}
            >
                <DateTimeRangePicker
                    onClose={closeCustomRange}
                    onApply={(type, start, end) => {
                        onChange(type, start, end);
                        closeCustomRange();
                        // Dispatch custom event for components listening to date range changes
                        window.dispatchEvent(new CustomEvent('IBPDateRangeApplied', { 
                            detail: { type, start, end } 
                        }));
                    }}
                    initialRange={dateRangeType}
                    initialStartDate={customStartDate}
                    initialEndDate={customEndDate}
                    timezone={timezone}
                    fixedOptions={fixedOptions}
                    olderThan90DaysMode={olderThan90DaysMode}
                    showCustomRange={showCustomRange}
                />
            </Popover>
        </Box>
    );
};

export default DateTimeRangeFilter;
